// components/modals/SkillsForDomainModal.js - VERSION SANS CHAMP NIVEAU INITIAL
import {
  Check,
  Lightbulb,
  Plus,
  RefreshCw,
  Trash2,
  X
} from 'lucide-react';
import { FaSave } from "react-icons/fa";
import { useEffect, useMemo, useState } from 'react';
import { getDomainInfo } from '../hooks_suggestion/useDomainSuggestions';
import { useSkillSuggestions } from '../hooks_suggestion/useSkillSuggestions';
import colors from "../../../../../styles/colors_global";

export const SkillsForDomainModal = ({
  isOpen,
  onClose,
  domainInfo,
  skills, // Compétences actuellement définies dans le domaine (array of strings)
  onAddSkill, // Fonction pour ajouter une compétence
  onRemoveSkill // Fonction pour supprimer une compétence
}) => {
  // États pour gérer les changements temporaires
  const [tempSkillsAdded, setTempSkillsAdded] = useState([]);
  const [tempSkillsRemoved, setTempSkillsRemoved] = useState(new Set());
  const [newSkill, setNewSkill] = useState('');
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [editingSkillLevel, setEditingSkillLevel] = useState(null);

  const { getSkillSuggestions, createSkillFromSuggestion, loading, metadata } = useSkillSuggestions();

  // Effet pour initialiser les états temporaires lorsque le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setTempSkillsAdded([]);
      setTempSkillsRemoved(new Set());
      setNewSkill('');
      setIsSavingChanges(false);
      setEditingSkillLevel(null);
    }
  }, [isOpen]);

  // Retrieve the domain ID for suggestions
  const getDomainIdForSuggestions = () => {
    if (!domainInfo) {
      return null;
    }
    const jsonDomainInfo = getDomainInfo(domainInfo.name);
    return jsonDomainInfo?.id || domainInfo.id || null;
  };

  // Define a stable order for difficulty levels from metadata
  const skillLevelOrder = useMemo(() => {
    return metadata?.niveaux || ['debutant', 'intermediaire', 'avance', 'expert'];
  }, [metadata]);

  // Helper function to get level display class
  const getLevelClass = (level) => {
    switch (level) {
      case 'debutant':
      case 'Débutant':
        return 'bg-blue-100 text-blue-800';
      case 'intermediaire':
      case 'Intermédiaire':
        return 'bg-green-100 text-green-800';
      case 'avance':
      case 'Avancé':
        return 'bg-purple-100 text-purple-800';
      case 'expert':
      case 'Expert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // NOUVEAU : Fonction pour gérer le changement de niveau d'une compétence
  const handleLevelChange = (skillName, newLevel) => {
    // Trouver la compétence dans tempSkillsAdded
    setTempSkillsAdded(prev => prev.map(skillObj => {
      const name = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
      if (name === skillName) {
        const normalizedLevel = newLevel === 'debutant' ? 'Débutant' :
                               newLevel === 'intermediaire' ? 'Intermédiaire' :
                               newLevel === 'avance' ? 'Avancé' :
                               newLevel === 'expert' ? 'Expert' : 'Débutant';
        
        return {
          ...skillObj,
          niveau_maitrise_requis: normalizedLevel
        };
      }
      return skillObj;
    }));
    
    setEditingSkillLevel(null);
  };

  // NOUVEAU : Composant popup pour sélection de niveau
  const LevelSelector = ({ skillName, currentLevel, onSelect, onClose }) => {
    const levels = [
      { value: 'debutant', label: 'Débutant', class: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
      { value: 'intermediaire', label: 'Intermédiaire', class: 'bg-green-100 text-green-800 hover:bg-green-200' },
      { value: 'avance', label: 'Avancé', class: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
      { value: 'expert', label: 'Expert', class: 'bg-red-100 text-red-800 hover:bg-red-200' }
    ];

    return (
      <div className="fixed inset-0 z-50" onClick={onClose}>
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        <div 
          className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[150px]"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-xs font-medium text-gray-600 mb-2 px-2">
            Choisir le niveau requis :
          </div>
          {levels.map(level => (
            <button
              key={level.value}
              onClick={() => onSelect(skillName, level.value)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${level.class} mb-1 last:mb-0`}
            >
              {level.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Calculate the current list of skills (with temporary changes)
  const currentSkillNames = useMemo(() => {
    let current = [...skills];
    current = current.filter(skillName => !tempSkillsRemoved.has(skillName));
    const tempNames = tempSkillsAdded.map(skillObj => 
      typeof skillObj === 'string' ? skillObj : skillObj.nom_skill || skillObj.nom
    );
    current = [...current, ...tempNames];
    return current;
  }, [skills, tempSkillsAdded, tempSkillsRemoved]);

  // Retrieve skill suggestions
  const getSuggestedSkills = useMemo(() => {
    const domainId = getDomainIdForSuggestions();
    if (!domainId || loading) {
      return [];
    }

    try {
      const suggestions = getSkillSuggestions(domainId);
      if (!suggestions || suggestions.length === 0) {
        return [];
      }

      const filteredSuggestions = suggestions.filter(skill =>
        !currentSkillNames.includes(skill.nom) && !currentSkillNames.includes(skill.nom_skill)
      );

      filteredSuggestions.sort((a, b) => {
        const levelA = skillLevelOrder.indexOf(a.niveau);
        const levelB = skillLevelOrder.indexOf(b.niveau);
        return levelA - levelB;
      });

      return filteredSuggestions;
    } catch (error) {
      return [];
    }
  }, [domainInfo, loading, currentSkillNames, getSkillSuggestions, skillLevelOrder]);

  // Function to add a custom skill temporarily
  const addCustomSkillTemp = () => {
    const skillName = newSkill.trim();
    if (!skillName) return;

    if (currentSkillNames.includes(skillName)) {
      alert('Cette compétence existe déjà dans votre liste ou est en attente d\'ajout/suppression !');
      return;
    }

    if (tempSkillsRemoved.has(skillName)) {
      setTempSkillsRemoved(prev => {
        const newSet = new Set(prev);
        newSet.delete(skillName);
        return newSet;
      });
      
      const customSkillObj = {
        nom_skill: skillName,
        description: '',
        niveau_maitrise_requis: 'Débutant', // Niveau par défaut
        training_link: '',
        certification: '',
        rating: 0,
        ordre_affichage: 0,
        isCustom: true
      };
      
      setTempSkillsAdded(prev => [...prev, customSkillObj]);
      setNewSkill('');
      return;
    }

    const customSkillObj = {
      nom_skill: skillName,
      description: '',
      niveau_maitrise_requis: 'Débutant', // Niveau par défaut
      training_link: '',
      certification: '',
      rating: 0,
      ordre_affichage: 0,
      isCustom: true
    };

    setTempSkillsAdded(prev => [...prev, customSkillObj]);
    setNewSkill('');
  };

  // Function to add a suggested skill temporarily
  const addSuggestedSkillTemp = (suggestion) => {
    const skillName = suggestion.nom_skill || suggestion.nom;
    
    if (currentSkillNames.includes(skillName)) {
      return;
    }

    if (tempSkillsRemoved.has(skillName)) {
      setTempSkillsRemoved(prev => {
        const newSet = new Set(prev);
        newSet.delete(skillName);
        return newSet;
      });
      
      const skillObj = createSkillFromSuggestion(suggestion, {
        rating: 0,
        ordre_affichage: 0
      });
      
      setTempSkillsAdded(prev => [...prev, skillObj]);
      return;
    }

    const skillObj = createSkillFromSuggestion(suggestion, {
      rating: 0,
      ordre_affichage: 0
    });

    setTempSkillsAdded(prev => [...prev, skillObj]);
  };

  // Function to temporarily remove a skill
  const removeSkillTemp = (skillName) => {
    const skillInTempAdded = tempSkillsAdded.find(skillObj => {
      const name = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
      return name === skillName;
    });
    
    if (skillInTempAdded) {
      setTempSkillsAdded(prev => prev.filter(skillObj => {
        const name = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
        return name !== skillName;
      }));
    }
    else if (skills.includes(skillName)) {
      setTempSkillsRemoved(prev => {
        const newSet = new Set(prev);
        newSet.add(skillName);
        return newSet;
      });
    }
  };

  // Calculate pending changes for display
  const pendingChanges = useMemo(() => {
    const changes = [];
    
    tempSkillsAdded.forEach(skillObj => {
      const skillName = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
      changes.push({
        name: skillName,
        skillData: skillObj,
        isNew: true,
        isPendingRemoval: false
      });
    });
    
    Array.from(tempSkillsRemoved).forEach(skill => {
      changes.push({
        name: skill,
        skillData: null,
        isNew: false,
        isPendingRemoval: true
      });
    });
    
    return changes;
  }, [tempSkillsAdded, tempSkillsRemoved]);

  const hasChanged = tempSkillsAdded.length > 0 || tempSkillsRemoved.size > 0;

  // FINAL SAVE LOGIC
  const handleSaveChanges = async () => {
    setIsSavingChanges(true);
    let allSucceeded = true;
    const errors = [];

    try {
      // 1. Supprimer les compétences marquées pour suppression
      const skillsToRemove = Array.from(tempSkillsRemoved);
      
      for (const skillName of skillsToRemove) {
        try {
          if (onRemoveSkill && typeof onRemoveSkill === 'function') {
            const result = await onRemoveSkill(skillName);
            console.log('Résultat suppression:', result);
          } else {
            throw new Error('Fonction onRemoveSkill non disponible');
          }
        } catch (error) {
          const errorMsg = `Erreur suppression ${skillName}: ${error.message}`;
          errors.push(errorMsg);
          allSucceeded = false;
        }
      }

      // 2. Ajouter les nouvelles compétences
      for (const skillObj of tempSkillsAdded) {
        try {
          const skillName = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
          
          if (onAddSkill && typeof onAddSkill === 'function') {
            const result = await onAddSkill(skillObj);
            console.log('Résultat ajout:', result);
          } else {
            throw new Error('Fonction onAddSkill non disponible');
          }
        } catch (error) {
          const skillName = typeof skillObj === 'string' ? skillObj : (skillObj.nom_skill || skillObj.nom);
          const errorMsg = `Erreur ajout ${skillName}: ${error.message}`;
          errors.push(errorMsg);
          allSucceeded = false;
        }
      }

      if (!allSucceeded) {
        const errorMessage = `Attention: Certaines opérations ont échoué:\n${errors.join('\n')}`;
        alert(errorMessage);
      }
    } catch (error) {
      const errorMessage = `Erreur critique: ${error.message}`;
      alert(errorMessage);
    } finally {
      setIsSavingChanges(false);
      
      if (allSucceeded) {
        onClose();
        setTempSkillsAdded([]);
        setTempSkillsRemoved(new Set());
        setNewSkill('');
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  if (!domainInfo) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur</h3>
            <p className="text-gray-600 mb-4">Aucun domaine sélectionné</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#6b7280] text-white rounded-lg hover:bg-[#4b5563]"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header épuré */}
        <div className="bg-gray-100 border-b border-gray-200 px-8 py-3">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-medium text-gray-900 tracking-tight">Compétences - {domainInfo.name}</h2>
            <button
              onClick={onClose}
              disabled={isSavingChanges}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-8 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Ajouter une compétence personnalisée */}
          <div className="mb-6">
            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
              Ajouter une Compétence Personnalisée
            </h3>
            <div className="flex space-x-3">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isSavingChanges && addCustomSkillTemp()}
                placeholder="Ex: Analyse de volatilité, Modèles de pricing..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#18574F] focus:border-transparent text-sm"
                disabled={isSavingChanges}
              />
              <button
                onClick={addCustomSkillTemp}
                disabled={!newSkill.trim() || isSavingChanges}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center border border-gray-200 font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Les compétences personnalisées sont ajoutées avec le niveau "Débutant" par défaut. Vous pourrez modifier le niveau en cliquant sur le badge une fois ajoutée.
            </p>
          </div>

          {/* Modifications en attente */}
          {pendingChanges.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
                Modifications en Attente
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  ({pendingChanges.length})
                </span>
              </h3>
              <div className="space-y-2">
                {pendingChanges.map((change, index) => {
                  const isPendingRemoval = change.isPendingRemoval;
                  const isNew = change.isNew;
                  const skillData = change.skillData;
                  
                  return (
                    <div key={`${change.name}-${index}`} className={`flex items-center justify-between p-2 rounded-lg group
                      ${isPendingRemoval ? 'bg-red-50 border border-red-200 opacity-60 line-through' :
                        isNew ? 'bg-[#c8d7d6]' : ''}`}>
                      <div className="flex items-center space-x-3">
                        {!isPendingRemoval && (
                          <RefreshCw className="w-4 h-4 text-teal-700 flex-shrink-0" />
                        )}
                        <div>
                          <span className="text-gray-900 text-sm font-medium">{change.name}</span>
                          {isNew && skillData && skillData.niveau_maitrise_requis && (
                            <div className="flex items-center space-x-2 mt-1">
                              <button
                                onClick={() => setEditingSkillLevel(change.name)}
                                className={`text-xs px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${getLevelClass(skillData.niveau_maitrise_requis)}`}
                                disabled={isSavingChanges}
                                title="Cliquer pour changer le niveau"
                              >
                                Niveau requis: {skillData.niveau_maitrise_requis}
                              </button>
                              {skillData.isCustom && (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                  Personnalisée
                                </span>
                              )}
                            </div>
                          )}
                          {isPendingRemoval && (
                            <div className="mt-1">
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                                En attente de suppression
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isPendingRemoval ? (
                        <button
                          onClick={() => {
                            setTempSkillsRemoved(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(change.name);
                              return newSet;
                            });
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-all flex items-center"
                          disabled={isSavingChanges}
                        >
                          <X className="w-3 h-3 mr-1" />Annuler
                        </button>
                      ) : (
                        <button
                          onClick={() => removeSkillTemp(change.name)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all p-1 hover:bg-red-100 rounded"
                          disabled={isSavingChanges}
                          title={`Supprimer "${change.name}"`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Suggestions de compétences */}
          <div>
            <h3 className="text-xl font-medium text-gray-900 mb-4 tracking-tight">
              Compétences Suggérées
              {loading && (
                <span className="ml-2 text-sm text-gray-500 font-normal">(Chargement...)</span>
              )}
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {getSuggestedSkills.map((suggestion) => (
                <div key={suggestion.id} className="flex items-center justify-between p-2 bg-[#fff3e6] rounded-lg hover:bg-[#ffad65] transition-colors group cursor-pointer border-2 border-dashed border-orange-300 hover:border-orange-400"
                  onClick={() => addSuggestedSkillTemp(suggestion)}>
                  <div className="flex items-center space-x-3">
                    <Lightbulb className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <span className="text-gray-900 text-sm font-medium">{suggestion.nom_skill || suggestion.nom}</span>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{suggestion.description}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getLevelClass(suggestion.niveau_maitrise_requis || suggestion.niveau)}`}>
                          {(suggestion.niveau_maitrise_requis || suggestion.niveau || 'debutant').charAt(0).toUpperCase() + (suggestion.niveau_maitrise_requis || suggestion.niveau || 'debutant').slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Plus className="w-4 h-4 text-orange-500 group-hover:text-orange-700 group-hover:scale-110 transition-all" />
                </div>
              ))}
            </div>
            {!loading && getSuggestedSkills.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium mb-2">Aucune suggestion disponible</p>
                <p className="text-sm">
                  Toutes les suggestions ont été ajoutées ou aucune suggestion n'est disponible pour ce domaine.
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Vous pouvez ajouter vos propres compétences dans la section ci-dessus.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-8 py-2 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600 font-medium">
              {(tempSkillsAdded.length > 0 || tempSkillsRemoved.size > 0) ? (
                <span>
                  {tempSkillsAdded.length} ajout{tempSkillsAdded.length > 1 ? 's' : ''} • {' '}
                  {tempSkillsRemoved.size} suppression{tempSkillsRemoved.size > 1 ? 's' : ''}
                </span>
              ) : (
                <span>Aucun changement en attente</span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isSavingChanges}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={!hasChanged || isSavingChanges}
                className="px-8 py-2 rounded-lg text-sm flex items-center justify-center font-medium"
                style={{
                  backgroundColor: hasChanged ? colors.green : colors.lightGreen,
                  color: "white",
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled && hasChanged) {
                    e.target.style.backgroundColor = colors.green;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = hasChanged ? colors.green : colors.lightGreen;
                  }
                }}
              >
                <FaSave className="mr-2" />
                {isSavingChanges ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* NOUVEAU : Popup de sélection de niveau */}
      {editingSkillLevel && (
        <LevelSelector
          skillName={editingSkillLevel}
          currentLevel={tempSkillsAdded.find(skill => 
            (typeof skill === 'string' ? skill : (skill.nom_skill || skill.nom)) === editingSkillLevel
          )?.niveau_maitrise_requis}
          onSelect={handleLevelChange}
          onClose={() => setEditingSkillLevel(null)}
        />
      )}
    </div>
  );
};